import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, Calendar, ArrowRight, ArrowUpRight, ChevronRight, Home as HomeIcon, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS } from "@/data/blog-posts";
import { PAGE_LABELS } from "@/data/seo-pages";
import { DOCTOR } from "@/data/content";
import { postSchemas, removePrerenderedSchemas } from "@/lib/schema-helpers";
import NotFound from "@/pages/NotFound";
import { renderRichText } from "@/components/RichText";
import { useBooking } from "@/context/BookingContext";

const BASE_URL = "https://bestspinesurgeonhyderabad.com";

const useBlogSeo = (post) => {
  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = post.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", post.metaDescription);
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.setAttribute("href", `${BASE_URL}/blog/${post.slug}`);

    const schema = postSchemas(post);
    removePrerenderedSchemas();
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-blog-post", post.slug);
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
      document.head.removeChild(el);
    };
  }, [post]);
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];
  const { openBooking } = useBooking();

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useBlogSeo(post);

  if (!post) return <NotFound />;

  return (
    <div data-testid="blog-post" className="bg-white">
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-14 border-b border-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_85%_-10%,#e8f1fb_0%,#f4f8fd_45%,#ffffff_80%)]" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center flex-wrap gap-1 text-sm text-slate-500" data-testid="blog-post-breadcrumbs">
            <Link to="/" className="flex items-center gap-1 hover:text-brand transition-colors">
              <HomeIcon className="w-3.5 h-3.5" /> Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/blog" className="hover:text-brand transition-colors" data-testid="blog-back-link">Blog</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#0b2c4d] font-medium">{post.category}</span>
          </nav>
          <span className="text-[10px] uppercase tracking-wider text-brand font-bold bg-brand-light px-2.5 py-1 rounded-full">
            {post.category}
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-[#0b2c4d] text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.06]">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-5 text-slate-400 text-sm">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.minutes}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Updated {post.date}</span>
          </div>
          <p className="mt-4 text-sm text-slate-500">
            By <span className="font-semibold text-[#0b2c4d]">{DOCTOR.name}</span> — Neurosurgeon &amp; Endoscopic Spine Surgeon, CARE Hospitals, Gachibowli
          </p>
        </div>
      </section>

      <article className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="rounded-2xl overflow-hidden border border-slate-200 mb-10">
            <img src={post.image} alt={post.title} className="w-full h-64 md:h-80 object-cover" />
          </div>

          <p className="text-lg text-slate-700 leading-relaxed">{post.intro}</p>

          {post.sections.map((s) => (
            <div key={s.heading} className="mt-10">
              <h2 className="font-heading font-extrabold text-[#0b2c4d] text-2xl md:text-3xl tracking-tight">
                {s.heading}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">{renderRichText(s.body)}</p>
            </div>
          ))}

          <div className="mt-12 rounded-2xl bg-brand-light border border-brand/15 p-6 md:p-8" data-testid="blog-takeaways">
            <h2 className="font-heading font-extrabold text-[#0b2c4d] text-xl tracking-tight">Key takeaways</h2>
            <ul className="mt-4 space-y-3">
              {post.takeaways.map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-8 text-xs text-slate-400">
            Educational information only — not a substitute for professional medical advice, diagnosis or treatment.
          </p>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-slate-500">
              Have symptoms you would like assessed?
            </div>
            <Button
              onClick={() => openBooking("appointment")}
              data-testid="blog-book-btn"
              className="bg-brand hover:bg-brand-hover text-white font-semibold rounded-full group"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </article>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Keep reading</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-3xl md:text-4xl tracking-tight mt-3">
            Related reading
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {post.related.map((r) => (
              <Link
                key={r}
                to={`/blog/${r}`}
                data-testid={`blog-related-${r}`}
                className="group glass-card rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <div className="text-xs uppercase tracking-wider text-brand font-bold">{BLOG_POSTS[r].category}</div>
                <h3 className="mt-2 font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug">
                  {BLOG_POSTS[r].title}
                </h3>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                  Read Article
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
            {(post.relatedPages || []).map((r) => (
              <Link
                key={r}
                to={`/${r}`}
                data-testid={`blog-related-page-${r}`}
                className="group glass-card rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <div className="text-xs uppercase tracking-wider text-brand font-bold">Treatment Page</div>
                <h3 className="mt-2 font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight leading-snug">
                  {PAGE_LABELS[r]}
                </h3>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                  Learn more
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
